import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import { AuthLayout } from '@/components/AuthLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const response = await fetch(`${apiUrl}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setSent(true);
      } else {
        setError(data.error || t('errors.generic', { defaultValue: 'Something went wrong. Try again.' }));
      }
    } catch {
      setError(t('errors.networkError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
      <Link to="/login" className="text-link text-sm">
        <ArrowLeft className="od-icon" />
        {t('auth.backToSignIn', { defaultValue: 'Back to sign in' })}
      </Link>
      <p className="page-eyebrow">{t('auth.accountRecovery', { defaultValue: 'Account recovery' })}</p>
      <h1>{t('auth.recoverAccount', { defaultValue: 'Recover your account' })}</h1>
      <p className="auth-description">
        {t('auth.recoverDescription', { defaultValue: 'Enter your account email to request recovery instructions.' })}
      </p>

      {sent ? (
        <div className="request-notice success !m-0" role="status">
          <CheckCircle2 className="od-icon" />
          <span>
            {t('auth.recoverySent', {
              defaultValue: 'If an account exists for that address, recovery instructions are on their way.',
            })}
          </span>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="od-field [--od-gap:8px]">
            <Label htmlFor="email">{t('auth.email')}</Label>
            <Input
              id="email"
              type="email"
              placeholder={t('auth.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              disabled={isLoading}
            />
          </div>

          {error && (
            <div className="request-notice error !m-0" role="alert">
              <AlertCircle className="od-icon" />
              <span>{error}</span>
            </div>
          )}

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading && <Loader2 className="od-icon mr-2 animate-spin" />}
            {t('auth.sendRecovery', { defaultValue: 'Send recovery instructions' })}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export default ForgotPassword;
